/**
 * Trips — every trip-scoped read and write in the app.
 *
 * Signatures mirror @travelix/core one-for-one. Real trips pass straight
 * through to Firestore; the onboarding sample trip (DEMO_TRIP_ID) is served
 * from the in-memory demo store, so the tutorial is hands-on without writing
 * anything. Actions that only make sense with real people (leaving,
 * deleting) return a friendly error the screens already toast.
 */

import * as core from "@travelix/core";
import { Timestamp } from "@travelix/core";
import type {
  Activity,
  ActivityCreate,
  ActivityUpdate,
  CascadingTripUpdate,
  DbResult,
  ExpenseEntry,
  PackingItem,
  Trip,
  TripBudget,
  TripIdea,
  TripPoll,
} from "@travelix/core";

import { demoOwnerFromUser, isDemoTripId, type DemoOwner } from "@/lib/demo/demo-trip";
import { demoUnavailable, ok } from "./result";
import {
  emitDemoEvent,
  ensureDemo,
  setLastCreatedActivityId,
  updateDemoActivities,
  updateDemoTrip,
} from "@/lib/demo/demo-store";

export { isDemoTripId };

// Nothing demo-specific about creating a trip — straight re-export.
export { createTripWithUser } from "@travelix/core";

// ─── Demo helpers ────────────────────────────────────────────

function currentOwner(): DemoOwner {
  return demoOwnerFromUser(core.getFirebaseAuth().currentUser);
}

/** Apply a patch to the sample trip and bump updatedAt. */
function patchDemoTrip(fn: (trip: Trip) => Partial<Trip>): DbResult<void> {
  ensureDemo(currentOwner());
  updateDemoTrip((t) => ({ ...t, ...fn(t), updatedAt: Timestamp.now() }));
  return ok(undefined);
}

function demoId(): string {
  return `demo-${crypto.randomUUID()}`;
}

// ─── Reads ───────────────────────────────────────────────────

export async function getTripById(tripId: string): Promise<DbResult<Trip>> {
  if (isDemoTripId(tripId)) return ok(ensureDemo(currentOwner()).trip);
  return core.getTripById(tripId);
}

export async function getTripActivities(tripId: string): Promise<DbResult<Activity[]>> {
  if (isDemoTripId(tripId)) {
    const activities = [...ensureDemo(currentOwner()).activities];
    // Mirror Firestore's orderBy("startTime").
    activities.sort(
      (a, b) => (a.startTime?.toMillis() ?? 0) - (b.startTime?.toMillis() ?? 0),
    );
    return ok(activities);
  }
  return core.getTripActivities(tripId);
}

// ─── Trip fields ─────────────────────────────────────────────

export async function updateTripSummaryFields(
  tripId: string,
  updates: CascadingTripUpdate,
): Promise<DbResult<void>> {
  if (isDemoTripId(tripId)) {
    return patchDemoTrip((t) => {
      const startDate = updates.startDate ?? t.startDate;
      const endDate = updates.endDate ?? t.endDate;
      return {
        ...updates,
        durationDays: Math.ceil((endDate.toMillis() - startDate.toMillis()) / 86_400_000),
      };
    });
  }
  return core.updateTripSummaryFields(tripId, updates);
}

export async function updateTripName(tripId: string, newName: string): Promise<DbResult<void>> {
  if (isDemoTripId(tripId)) return patchDemoTrip(() => ({ name: newName }));
  return core.updateTripName(tripId, newName);
}

export async function updateTripCurrency(
  tripId: string,
  currency: string,
): Promise<DbResult<void>> {
  if (isDemoTripId(tripId)) return patchDemoTrip(() => ({ currency }));
  return core.updateTripCurrency(tripId, currency);
}

export async function updateTripBudget(
  tripId: string,
  budget: TripBudget | null,
): Promise<DbResult<void>> {
  if (isDemoTripId(tripId)) return patchDemoTrip(() => ({ budget: budget ?? undefined }));
  return core.updateTripBudget(tripId, budget);
}

export async function setTripDayMapsUrl(
  tripId: string,
  dayKey: string,
  mapsUrl: string | null,
): Promise<DbResult<void>> {
  if (isDemoTripId(tripId)) {
    return patchDemoTrip((t) => {
      const days = { ...(t.days ?? {}) };
      if (mapsUrl) days[dayKey] = { ...days[dayKey], mapsUrl };
      else if (days[dayKey]) {
        const { mapsUrl: _drop, ...rest } = days[dayKey]!;
        days[dayKey] = rest;
      }
      return { days };
    });
  }
  return core.setTripDayMapsUrl(tripId, dayKey, mapsUrl);
}

// ─── Members (not available on the sample) ───────────────────

export async function removeMemberFromTrip(
  tripId: string,
  memberUid: string,
  memberEmail: string,
): Promise<DbResult<void>> {
  if (isDemoTripId(tripId)) return demoUnavailable();
  return core.removeMemberFromTrip(tripId, memberUid, memberEmail);
}

export async function leaveTrip(
  trip: Trip,
  userUid: string,
  userEmail: string,
): Promise<DbResult<void>> {
  if (isDemoTripId(trip.id)) return demoUnavailable();
  return core.leaveTrip(trip, userUid, userEmail);
}

export async function deleteTrip(
  tripId: string,
  memberEmails: string[],
): Promise<DbResult<void>> {
  if (isDemoTripId(tripId)) return demoUnavailable();
  return core.deleteTrip(tripId, memberEmails);
}

// ─── Activities ──────────────────────────────────────────────

export async function createActivity(
  tripId: string,
  data: ActivityCreate,
): Promise<DbResult<Activity>> {
  if (isDemoTripId(tripId)) {
    ensureDemo(currentOwner());
    const activity: Activity = {
      description: null,
      place: null,
      startTime: null,
      endTime: null,
      durationHours: null,
      latitude: null,
      longitude: null,
      category: null,
      ...data,
      id: demoId(),
    };
    updateDemoActivities((list) => [...list, activity]);
    setLastCreatedActivityId(activity.id);
    emitDemoEvent("activity:created");
    return ok(activity);
  }
  return core.createActivity(tripId, data);
}

export async function updateActivity(
  tripId: string,
  activityId: string,
  data: ActivityUpdate,
): Promise<DbResult<void>> {
  if (isDemoTripId(tripId)) {
    ensureDemo(currentOwner());
    updateDemoActivities((list) =>
      list.map((a) => (a.id === activityId ? { ...a, ...data } : a)),
    );
    return ok(undefined);
  }
  return core.updateActivity(tripId, activityId, data);
}

export async function deleteActivity(
  tripId: string,
  activityId: string,
): Promise<DbResult<void>> {
  if (isDemoTripId(tripId)) {
    ensureDemo(currentOwner());
    updateDemoActivities((list) => list.filter((a) => a.id !== activityId));
    return ok(undefined);
  }
  return core.deleteActivity(tripId, activityId);
}

// ─── Expenses ────────────────────────────────────────────────

export async function addExpenseToTrip(
  tripId: string,
  expense: ExpenseEntry,
): Promise<DbResult<void>> {
  if (isDemoTripId(tripId)) {
    const result = patchDemoTrip((t) => ({ expenses: [...t.expenses, expense] }));
    emitDemoEvent("expense:added");
    return result;
  }
  return core.addExpenseToTrip(tripId, expense);
}

export async function removeExpenseFromTrip(
  tripId: string,
  expenseId: string,
): Promise<DbResult<void>> {
  if (isDemoTripId(tripId)) {
    return patchDemoTrip((t) => ({ expenses: t.expenses.filter((e) => e.id !== expenseId) }));
  }
  return core.removeExpenseFromTrip(tripId, expenseId);
}

export async function updateExpenseInTrip(
  tripId: string,
  updated: ExpenseEntry,
): Promise<DbResult<void>> {
  if (isDemoTripId(tripId)) {
    return patchDemoTrip((t) => ({
      expenses: t.expenses.map((e) =>
        e.id === updated.id ? { ...updated, updatedAt: Timestamp.now() } : e,
      ),
    }));
  }
  return core.updateExpenseInTrip(tripId, updated);
}

// ─── Ideas ───────────────────────────────────────────────────

export async function addTripIdea(tripId: string, idea: TripIdea): Promise<DbResult<void>> {
  if (isDemoTripId(tripId)) {
    const result = patchDemoTrip((t) => ({ ideas: [...(t.ideas ?? []), idea] }));
    emitDemoEvent("idea:added");
    return result;
  }
  return core.addTripIdea(tripId, idea);
}

export async function removeTripIdea(tripId: string, ideaId: string): Promise<DbResult<void>> {
  if (isDemoTripId(tripId)) {
    return patchDemoTrip((t) => ({ ideas: (t.ideas ?? []).filter((i) => i.id !== ideaId) }));
  }
  return core.removeTripIdea(tripId, ideaId);
}

export async function markIdeaScheduled(
  tripId: string,
  ideaId: string,
  activityId: string,
): Promise<DbResult<void>> {
  if (isDemoTripId(tripId)) {
    return patchDemoTrip((t) => ({
      ideas: (t.ideas ?? []).map((i) =>
        i.id === ideaId ? { ...i, scheduledActivityId: activityId } : i,
      ),
    }));
  }
  return core.markIdeaScheduled(tripId, ideaId, activityId);
}

// ─── Polls ───────────────────────────────────────────────────

export async function addTripPoll(tripId: string, poll: TripPoll): Promise<DbResult<void>> {
  if (isDemoTripId(tripId)) {
    return patchDemoTrip((t) => ({ polls: [...(t.polls ?? []), poll] }));
  }
  return core.addTripPoll(tripId, poll);
}

export async function castPollVote(
  tripId: string,
  pollId: string,
  userId: string,
  optionId: string,
): Promise<DbResult<void>> {
  if (isDemoTripId(tripId)) {
    const result = patchDemoTrip((t) => ({
      polls: (t.polls ?? []).map((p) =>
        p.id === pollId && !p.closed ? { ...p, votes: { ...p.votes, [userId]: optionId } } : p,
      ),
    }));
    emitDemoEvent("poll:voted");
    return result;
  }
  return core.castPollVote(tripId, pollId, userId, optionId);
}

export async function closeTripPoll(tripId: string, pollId: string): Promise<DbResult<void>> {
  if (isDemoTripId(tripId)) {
    return patchDemoTrip((t) => ({
      polls: (t.polls ?? []).map((p) => (p.id === pollId ? { ...p, closed: true } : p)),
    }));
  }
  return core.closeTripPoll(tripId, pollId);
}

export async function removeTripPoll(tripId: string, pollId: string): Promise<DbResult<void>> {
  if (isDemoTripId(tripId)) {
    return patchDemoTrip((t) => ({ polls: (t.polls ?? []).filter((p) => p.id !== pollId) }));
  }
  return core.removeTripPoll(tripId, pollId);
}

// ─── Packing ─────────────────────────────────────────────────

export async function addPackingItem(tripId: string, item: PackingItem): Promise<DbResult<void>> {
  if (isDemoTripId(tripId)) {
    return patchDemoTrip((t) => ({ packingItems: [...(t.packingItems ?? []), item] }));
  }
  return core.addPackingItem(tripId, item);
}

export async function claimPackingItem(
  tripId: string,
  itemId: string,
  userId: string,
  userName: string,
): Promise<DbResult<void>> {
  if (isDemoTripId(tripId)) {
    const result = patchDemoTrip((t) => ({
      packingItems: (t.packingItems ?? []).map((i) =>
        i.id === itemId
          ? { ...i, claimedBy: userId, claimedByName: userName, claimedAt: Timestamp.now() }
          : i,
      ),
    }));
    emitDemoEvent("packing:claimed");
    return result;
  }
  return core.claimPackingItem(tripId, itemId, userId, userName);
}

export async function unclaimPackingItem(tripId: string, itemId: string): Promise<DbResult<void>> {
  if (isDemoTripId(tripId)) {
    return patchDemoTrip((t) => ({
      packingItems: (t.packingItems ?? []).map((i) => {
        if (i.id !== itemId) return i;
        const { claimedBy: _c, claimedByName: _n, claimedAt: _a, ...rest } = i;
        return rest;
      }),
    }));
  }
  return core.unclaimPackingItem(tripId, itemId);
}

export async function removePackingItem(tripId: string, itemId: string): Promise<DbResult<void>> {
  if (isDemoTripId(tripId)) {
    return patchDemoTrip((t) => ({
      packingItems: (t.packingItems ?? []).filter((i) => i.id !== itemId),
    }));
  }
  return core.removePackingItem(tripId, itemId);
}
